import React, { useState } from "react";
import {
  type ProcessedRaceMeeting,
  useSuggestedMeetings,
} from "@/hooks/firestore/v2/betting/useRaceMeetings";
import { TitleIcon } from "@/components/TitleIcon";
import { useTitle } from "@/hooks/graphql/useTitles";
import {
  Popover,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
} from "components/Popover";
import { Typography } from "@/components/Typography";
import { CountryAndState } from "@/features/racing/components/CountryAndState";
import { MoreInfo } from "@/components/MoreInfo";
import { RaceWeather } from "@/features/racing/components/RaceWeather";
import { Box } from "@/components/Box";
import { ScrollArea } from "@/components/ScrollArea";
import { Link } from "@/components/Link";
import { Caret } from "@/components/Caret";
import cx from "classnames";
import { EventStatus } from "@/components/layout/EventStatus";
import { ReactComponent as TickIcon } from "components/assets/tick.svg";
import { SmallPromoIcon } from "@/components/PromoIcon/SmallPromoIcon";
import { useIsLoggedIn } from "@/hooks/useIsLoggedIn";

import * as styles from "./RaceMeetingHeader.module.scss";

type RaceMeetingHeaderProps = {
  meeting: ProcessedRaceMeeting;
};

const RaceMeetingHeader = ({ meeting }: RaceMeetingHeaderProps) => {
  const title = useTitle(meeting.sport);
  const isLoggedIn = useIsLoggedIn();
  const [isOpen, setIsOpen] = useState(false);
  const { meetings: suggestedMeetings } = useSuggestedMeetings(
    meeting.scheduledStartTimeTs.toDate(),
    meeting.region,
    meeting.sport,
  );

  return (
    <div className={styles.container}>
      <div className={styles.icon}>
        <TitleIcon title={title} svg />
      </div>
      <Popover open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
        <PopoverTrigger asChild>
          <div className={styles.title}>
            <div className={styles.venue}>
              <Typography variant={`h2`} noSpacing className={styles.venue}>
                {meeting.venue}
              </Typography>
              {suggestedMeetings && suggestedMeetings.length > 0 ? (
                <MoreInfo className={styles.trigger} direction={`vertical`} />
              ) : null}
            </div>
            <Typography variant={`body`} noSpacing>
              <CountryAndState
                countryCode={meeting.country}
                stateCode={meeting.state}
              />
            </Typography>
          </div>
        </PopoverTrigger>
        {suggestedMeetings ? (
          <PopoverContent
            className={styles.popover}
            align={"center"}
            collisionPadding={15}
          >
            <Box hideHeader noChildrenSpace className={styles.meetings}>
              <ScrollArea darkScrollBar className={styles.scrollArea}>
                {suggestedMeetings.map((suggestedMeeting) => {
                  const isSelected = suggestedMeeting.id === meeting.id;
                  return (
                    <Link
                      key={`suggested-${suggestedMeeting.id}`}
                      className={styles.meeting}
                      to={`/racing/betting/meeting/${suggestedMeeting.id}/`}
                      onClick={() => setIsOpen(false)}
                    >
                      <div className={styles.indicator}>
                        {isSelected ? (
                          <TickIcon />
                        ) : (
                          <Caret variant="right" className={styles.caret} />
                        )}
                      </div>
                      <div
                        className={cx(styles.suggestedVenue, {
                          [styles.isSelected]: isSelected,
                        })}
                      >
                        {`${suggestedMeeting.venue}, R${suggestedMeeting.currentRace?.number}`}
                      </div>
                      <div className={styles.country}>
                        {suggestedMeeting.country}
                        {isLoggedIn &&
                        suggestedMeeting.currentRace.showPromoBanner ? (
                          <SmallPromoIcon />
                        ) : null}
                      </div>
                      <EventStatus
                        shape="rounded"
                        status={suggestedMeeting.currentRace.status as any}
                        scheduledStartTime={suggestedMeeting.currentRace.scheduledStartTimeTs.toDate()}
                      />
                    </Link>
                  );
                })}
              </ScrollArea>
            </Box>
            <PopoverArrow className={styles.arrow} />
          </PopoverContent>
        ) : null}
      </Popover>
      {meeting.weather ? (
        <div className={styles.weather}>
          <RaceWeather
            weather={meeting.weather}
            trackCondition={meeting.currentRace.trackCondition}
            surface={meeting.currentRace.surface}
            trackRating={meeting.trackRating}
          />
        </div>
      ) : null}
    </div>
  );
};

export { RaceMeetingHeader };
